import { useState, useEffect } from 'react';
import { toastService as toast } from '../../../services/toastService';
import { getApiErrorCode, extractApiErrorMessage } from '../../../utils/apiError';
import { useAuth } from '../../../hooks/useAuth';
import { useLanguage } from '../../../contexts/LanguageContext';
import { authService } from '../../../services/authService';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Eye, EyeOff, RefreshCw, Mail } from 'lucide-react';

interface SignInFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
}

export function SignInForm({ email, setEmail, password, setPassword }: SignInFormProps) {
  const { login } = useAuth();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Handle query params from email verification redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === 'true') {
      toast.success(t.auth.emailVerifiedSuccess ?? 'Email vérifié avec succès ! Vous pouvez maintenant vous connecter.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('verification') === 'expired') {
      toast.error(t.auth.emailVerificationExpired ?? 'Le lien de vérification a expiré. Veuillez vous reconnecter pour en recevoir un nouveau.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('verification') === 'invalid') {
      toast.error(t.auth.emailVerificationInvalid ?? 'Le lien de vérification est invalide.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setEmailNotVerified(false);
    try {
      await login({ email, password });
    } catch (err: unknown) {
      // V4-FRONT-ANY-USAGE batch 6 2026-05-29
      const code = getApiErrorCode(err);
      // V4-AUTH-EMAIL-NOT-VERIFIED-STRUCTURED-DETAIL — le backend émet désormais
      // detail={error, message} (comme account_inactive) au lieu d'un detail string
      // brut. getApiErrorCode() lit les deux formes ; getApiErrorDetail() ne lisait
      // QUE la forme string brute et ratait donc ce cas depuis le passage au detail
      // structuré, laissant tomber sur le toast générique 403 "Accès refusé" de
      // apiClient.ts (misleading — cf. STRIPE_TEST_PLAN.md phase 2, repro live S1).
      if (code === 'email_not_verified') {
        setEmailNotVerified(true);
        setError(t.auth.emailNotVerifiedError ?? 'Votre email n\'est pas encore vérifié. Vérifiez votre boîte de réception.');
      } else if (code === 'account_inactive') {
        // Compte désactivé / accès révoqué — message explicite (pas « identifiants
        // invalides » qui laisserait croire à un mauvais mot de passe). V4-AGENCY-GUEST-REVOKE-MSG.
        const msg = extractApiErrorMessage(err, t.auth.accountDeactivatedDescription);
        setError(msg);
        toast.warning(t.auth.accountDeactivatedTitle, msg);
      } else {
        setError(t.auth.errorInvalid);
        toast.error(t.auth.loginErrorTitle);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) return;
    setResendingEmail(true);
    try {
      await authService.resendVerificationEmail(email);
      toast.success(t.auth.verificationEmailResent ?? 'Email de vérification renvoyé !');
    } catch {
      toast.error(t.auth.verificationEmailResendError ?? 'Erreur lors de l\'envoi');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-sign-in-form">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[var(--color-text-primary)]">{t.auth.emailLabel}</Label>
            <Input
              id="email"
              data-testid="auth-email-input"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="px-4 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-[var(--color-text-primary)]">{t.auth.passwordLabel}</Label>
              <a href="/forgot-password" className="text-sm font-medium text-[var(--color-primary)] hover:underline cursor-pointer">{t.auth.forgotPassword}</a>
            </div>
            <div className="relative">
              <Input
                id="password"
                data-testid="auth-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="px-4 pr-14 bg-[var(--color-surface)] border-[var(--color-border)] focus:ring-[var(--color-primary)] transition-all"
              />
              <button type="button" data-testid="auth-password-toggle" onClick={() => setShowPassword(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer" style={{ color: 'var(--color-text-secondary)' }}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-red-50 text-sm text-red-600 font-medium border border-red-100 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
              {error}
            </div>
            {emailNotVerified && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingEmail || !email}
                className="auth-accent-soft flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 py-2 px-3 rounded-md"
              >
                {resendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {t.auth.resendVerificationEmail ?? 'Renvoyer l\'email de vérification'}
              </button>
            )}
            {/* 1.2 — mention spam explicite (parité avec l'écran d'inscription). */}
            {emailNotVerified && (
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {t.auth.checkSpamFolder ?? "Si vous ne trouvez pas l'email, pensez à vérifier votre dossier spam ou courriers indésirables."}
              </span>
            )}
          </div>
        )}

        <Button
          type="submit"
          className="auth-cta w-full h-11 text-base font-medium cursor-pointer"
          disabled={isLoading}
          data-testid="auth-submit-button"
        >
          {isLoading ? (
            <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t.auth.signingIn}</>
          ) : (
            t.auth.signInButton
          )}
        </Button>
      </form>
    </>
  );
}
